/**
 * Composant pour améliorer le maillage interne du site
 * Liens contextuels, suggestions et navigation recommandée
 */
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, HelpCircle, Calculator, FileText, Users, Scale } from "lucide-react";

interface RelatedLink {
  title: string;
  slug: string;
  category: string;
  description?: string;
  readTime?: string;
}

interface NavigationSuggestion {
  title: string;
  href: string;
  icon: React.ComponentType<any>;
  description: string;
  badge?: string;
}

export const InternalLinkingSidebar = ({ currentSlug }: { currentSlug?: string }) => {
  const navigationSuggestions: NavigationSuggestion[] = [
    {
      title: "Questions fréquentes", 
      href: "/faq",
      icon: HelpCircle,
      description: "Réponses aux questions les plus posées"
    },
    {
      title: "Calculer le prix",
      href: "/guides/prix-assurance-do-2025",
      icon: Calculator,
      description: "Estimez le coût de votre assurance DO"
    },
    {
      title: "Parler de mon projet",
      href: "/contact",
      icon: Users,
      description: "Accompagnement personnalisé gratuit",
      badge: "Gratuit"
    },
    {
      title: "Obligations légales",
      href: "/guides/assurance-do-obligatoire",
      icon: Scale,
      description: "Comprendre le cadre légal"
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Navigation utile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {navigationSuggestions.map((suggestion) => {
            const Icon = suggestion.icon;
            return (
              <Link
                key={suggestion.href}
                to={suggestion.href}
                className="block p-3 rounded-lg hover:bg-muted transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm group-hover:text-primary transition-colors">
                        {suggestion.title}
                      </h4>
                      {suggestion.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {suggestion.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {suggestion.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export const RelatedArticles = ({ 
  currentSlug, 
  currentCategory, 
  maxItems = 3 
}: { 
  currentSlug?: string; 
  currentCategory?: string;
  maxItems?: number;
}) => {
  // Articles recommandés basés sur les catégories populaires
  const relatedByCategory: Record<string, RelatedLink[]> = {
    "Cadre légal & obligations": [
      {
        title: "DO vs garantie décennale : les différences",
        slug: "dommages-ouvrage-vs-garantie-decennale", 
        category: "Cadre légal & obligations",
        description: "Comprendre ces deux assurances complémentaires",
        readTime: "8 min"
      },
      {
        title: "Responsabilité du maître d'ouvrage",
        slug: "responsabilite-maitre-ouvrage-do",
        category: "Cadre légal & obligations", 
        readTime: "7 min"
      },
      {
        title: "Vendre sans assurance DO : quels risques ?",
        slug: "revente-bien-sans-do",
        category: "Cadre légal & obligations",
        readTime: "8 min"
      }
    ],
    "Coûts & financement": [
      {
        title: "Prix d'une assurance DO en 2025",
        slug: "prix-assurance-do-2025",
        category: "Coûts & financement",
        description: "Calcul, barèmes et exemples concrets",
        readTime: "7 min"
      },
      {
        title: "Comment financer l'assurance DO",
        slug: "financer-assurance-do", 
        category: "Coûts & financement",
        readTime: "6 min"
      },
      {
        title: "DO et prêt immobilier : exigences banques",
        slug: "do-pret-immobilier-banques",
        category: "Coûts & financement",
        readTime: "6 min"
      }
    ],
    "Processus & démarches": [
      {
        title: "Comment souscrire une DO étape par étape",
        slug: "comment-souscrire-assurance-do",
        category: "Processus & démarches",
        description: "Guide complet avec tous les documents",
        readTime: "8 min"
      },
      {
        title: "Comment déclarer un sinistre DO",
        slug: "declarer-sinistre-do",
        category: "Processus & démarches", 
        readTime: "7 min"
      },
      {
        title: "Comment choisir un assureur DO",
        slug: "choisir-assureur-do",
        category: "Processus & démarches",
        readTime: "7 min"
      }
    ],
    "Types de projets": [
      {
        title: "DO pour maisons individuelles",
        slug: "assurance-do-maisons-individuelles",
        category: "Types de projets",
        readTime: "5 min"
      },
      {
        title: "DO pour extension de maison",
        slug: "assurance-do-extension",
        category: "Types de projets", 
        readTime: "5 min"
      },
      {
        title: "DO pour rénovation lourde",
        slug: "assurance-do-renovation-lourde",
        category: "Types de projets",
        readTime: "6 min"
      }
    ]
  };

  // Articles génériques populaires si pas de catégorie
  const popularArticles: RelatedLink[] = [
    {
      title: "Assurance DO : obligatoire ou pas ?",
      slug: "assurance-do-obligatoire",
      category: "Cadre légal & obligations",
      description: "La question de base à connaître",
      readTime: "5 min"
    },
    {
      title: "Prix d'une assurance DO en 2025",
      slug: "prix-assurance-do-2025", 
      category: "Coûts & financement",
      description: "Calcul et facteurs influençant le coût",
      readTime: "7 min"
    },
    {
      title: "Que couvre exactement l'assurance DO ?",
      slug: "que-couvre-assurance-do",
      category: "Cas pratiques & risques",
      description: "Périmètre précis de couverture",
      readTime: "7 min"
    }
  ];

  const getRelatedArticles = (): RelatedLink[] => {
    let articles: RelatedLink[] = [];
    
    if (currentCategory && relatedByCategory[currentCategory]) {
      articles = relatedByCategory[currentCategory];
    } else {
      articles = popularArticles;
    }
    
    // Filtrer l'article actuel et limiter le nombre
    return articles
      .filter(article => article.slug !== currentSlug)
      .slice(0, maxItems);
  };

  const relatedArticles = getRelatedArticles();

  if (relatedArticles.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Articles recommandés</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {relatedArticles.map((article) => (
          <Link
            key={article.slug}
            to={`/guides/${article.slug}`}
            className="block p-3 rounded-lg hover:bg-muted transition-colors group"
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {article.category}
                </Badge>
                {article.readTime && (
                  <span className="text-xs text-muted-foreground">
                    {article.readTime}
                  </span>
                )}
              </div>
              <h4 className="font-medium text-sm group-hover:text-primary transition-colors leading-tight">
                {article.title}
              </h4>
              {article.description && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {article.description}
                </p>
              )}
            </div>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
};

export const ContextualLinks = ({ pageType }: { pageType: 'home' | 'faq' | 'guides' | 'contact' | 'guide' }) => {
  const getContextualSuggestions = () => {
    switch (pageType) {
      case 'home':
        return [
          { text: "Questions fréquentes", href: "/faq", description: "Réponses rapides aux questions courantes" },
          { text: "Guide des prix 2025", href: "/guides/prix-assurance-do-2025", description: "Calculez le coût de votre DO" },
          { text: "Est-ce obligatoire ?", href: "/guides/assurance-do-obligatoire", description: "Comprendre l'obligation légale" }
        ];
      case 'faq':
        return [
          { text: "Tous nos guides", href: "/guides", description: "Approfondir vos connaissances" },
          { text: "Parler à un expert", href: "/contact", description: "Accompagnement personnalisé" },
          { text: "Comment souscrire", href: "/guides/comment-souscrire-assurance-do", description: "Guide étape par étape" }
        ];
      case 'guides':
        return [
          { text: "Questions courantes", href: "/faq", description: "Réponses rapides" },
          { text: "Être accompagné", href: "/contact", description: "Conseil personnalisé gratuit" },
          { text: "Obligations légales", href: "/guides/assurance-do-obligatoire", description: "Le cadre juridique" }
        ];
      case 'contact':
        return [
          { text: "Nos guides gratuits", href: "/guides", description: "Préparez votre projet" },
          { text: "Questions fréquentes", href: "/faq", description: "Réponses immédiates" },
          { text: "Calculer le prix", href: "/guides/prix-assurance-do-2025", description: "Estimation du coût" }
        ];
      default:
        return [];
    }
  };

  const suggestions = getContextualSuggestions();

  if (suggestions.length === 0) return null;

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <h3 className="font-semibold text-foreground mb-4">Poursuivre votre découverte</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion.href}
            asChild
            variant="ghost"
            className="h-auto p-4 justify-start text-left"
          >
            <Link to={suggestion.href}>
              <div>
                <div className="font-medium text-sm mb-1">{suggestion.text}</div>
                <div className="text-xs text-muted-foreground">{suggestion.description}</div>
              </div>
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
};

export const BreadcrumbNavigation = ({ 
  items 
}: { 
  items: Array<{ label: string; href?: string }> 
}) => {
  return (
    <nav className="flex items-center text-sm text-muted-foreground mb-6">
      <Link to="/" className="hover:text-primary transition-colors">
        Accueil
      </Link>
      {items.map((item, index) => (
        <div key={index} className="flex items-center">
          <span className="mx-2">/</span>
          {item.href ? (
            <Link to={item.href} className="hover:text-primary transition-colors">
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground">{item.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
};

export const CrossSellCTA = ({ 
  title, 
  description, 
  primaryAction, 
  secondaryAction 
}: {
  title: string;
  description: string;
  primaryAction: { text: string; href: string };
  secondaryAction?: { text: string; href: string };
}) => {
  return (
    <Card className="bg-gradient-primary text-primary-foreground">
      <CardContent className="p-6 text-center">
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-primary-foreground/80 mb-4">{description}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild variant="hero" className="bg-white text-primary hover:bg-white/90">
            <Link to={primaryAction.href}>
              {primaryAction.text}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          {secondaryAction && (
            <Button asChild variant="secondary" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-transparent">{/* Correction: utilise text-primary-foreground au lieu de text-white */}
              <Link to={secondaryAction.href}>
                {secondaryAction.text}
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};